import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import { Chatboxx as ChatboxxView } from "codekit";
import {
  useChatAgentMutation,
  useConfirmAgentMutation,
} from "../../libs/redux/slices/Agentexx/agentApiSlice";
import { getMenuUser } from "../../libs/redux/slices/authSlice/authSlice";
import { setModuleSelected, setProcesSelected } from "../../libs/redux/slices/MenuSlice/menuSlice";

const MAX_VISIBLE = 5;

/** Módulos con herramientas activas en el agente; ajustar aquí al habilitar más. */
const ACTIVE_MODULES = ["financix", "ordtraxx"];

/** Procesos con herramientas activas por módulo; ajustar aquí al habilitar más. */
const ACTIVE_PROCESSES = {
  financix: ["ofercome", "ctrlcupo", "dosptefa"],
  ordtraxx: ["tareasxx"],
};

/**
 * Sugerencias clicables por scope. Si la sugerencia tiene `field`, al pulsarla
 * se pide ese dato (DO, tarea, cliente) antes de enviar; si no, se envía directo.
 * `build(value)` arma el mensaje final con el dato capturado.
 */
const DEFAULT_QUESTIONS = {
  "financix.ctrlcupo": [
    {
      label: "¿Por qué un cliente no tiene cupo?",
      field: { placeholder: "Id o NIT del cliente" },
      build: (v) => `¿Por qué el cliente ${v} no tiene cupo disponible?`,
    },
    {
      label: "Consultar el cupo de un cliente",
      field: { placeholder: "Id o NIT del cliente" },
      build: (v) => `Consulta el cupo del cliente ${v}`,
    },
    { label: "¿Qué clientes tienen el cupo agotado?" },
  ],
  "financix.dosptefa": [
    {
      label: "¿Por qué no aparece un DO en facturación?",
      field: { placeholder: "Número de DO, p. ej. 2660087" },
      build: (v) => `¿Por qué no aparece el DO ${v} en facturación?`,
    },
    {
      label: "Ver la oferta y los rubros de un DO",
      field: { placeholder: "Número de DO" },
      build: (v) => `Muéstrame la oferta y los rubros del DO ${v}`,
    },
    {
      label: "Ver consecutivo de tarea de facturación",
      field: { placeholder: "Número de DO" },
      build: (v) => `Muéstrame el consecutivo de la tarea de facturación (CLATARID 138) del DO ${v}, con su responsable y estado`,
    },
    {
      label: "¿Cuándo se facturó un DO?",
      field: { placeholder: "Número de DO" },
      build: (v) => `¿Cuándo se facturó el DO ${v}? Dame el número y fecha de la factura`,
    },
    {
      label: "Escalar el caso a soporte",
      field: { placeholder: "Número de DO y problema, ej: 2660087 - no aparece en facturación" },
      build: (v) => `Escala este caso a soporte. ${v}`,
    },
  ],
  "financix.ofercome": [
    {
      label: "Buscar ofertas de un cliente",
      field: { placeholder: "Nombre del cliente" },
      build: (v) => `Busca las ofertas del cliente ${v}`,
    },
    {
      label: "Ver el detalle de una oferta",
      field: { placeholder: "Consecutivo de la oferta" },
      build: (v) => `Muéstrame el detalle de la oferta ${v}`,
    },
    {
      label: "Duplicar una oferta",
      field: { placeholder: "Consecutivo y versión, ej: 12345 1" },
      build: (v) => `Duplica la oferta ${v}`,
    },
    { label: "Ver mis ofertas más recientes" },
    {
      label: "Ver ofertas por estado",
      field: { placeholder: "Estado, ej: VENCIDA, RECHAZADA, ACEPTADA, BORRADOR, RENOVADA" },
      build: (v) => `Muéstrame las ofertas en estado ${v}`,
    },
    { label: "Ver ofertas pendientes de autorización" },
    { label: "Abrir el tracking de ofertas comerciales" },
  ],
  "ordtraxx.tareasxx": [
    {
      label: "¿Cuál es el estado de una tarea?",
      field: { placeholder: "Número de tarea" },
      build: (v) => `¿Cuál es el estado de la tarea ${v}?`,
    },
    { label: "¿Cuáles son mis tareas pendientes?" },
    { label: "Abrir el tracking de tareas" },
    {
      label: "Escalar el caso a soporte",
      field: { placeholder: "Número de tarea y problema, ej: 4521 - no cambia de estado" },
      build: (v) => `Escala este caso a soporte. ${v}`,
    },
  ],
};

/**
 * Chat del agente conversacional de CIMA. El scope de la conversación lo
 * elige el usuario seleccionando módulo y proceso al inicio. La presentación
 * (burbujas, sugerencias, picker) vive en el componente `Chatboxx` de codekit;
 * aquí solo se resuelven los datos y las acciones del agente.
 * @returns {JSX.Element}
 */
export default function Chatboxx() {
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [pending, setPending] = useState(null);
  const [navigate, setNavigate] = useState(null);
  const [scope, setScope] = useState(null);
  const [selModule, setSelModule] = useState(null);
  const [selProc, setSelProc] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [chatAgent, { isLoading: sending }] = useChatAgentMutation();
  const [confirmAgent, { isLoading: confirming }] = useConfirmAgentMutation();
  const menuData = useSelector(getMenuUser);
  const dispatch = useDispatch();

  /** Aplica la respuesta del backend al estado. @param {object} data Respuesta @returns {void} */
  const applyResult = (data) => {
    setConversationId(data.conversationId);
    if (data.reply) setMessages((prev) => [...prev, { role: "assistant", text: data.reply }]);
    if (data.pending) setPending(data.pending);
    if (data.navigate) setNavigate(data.navigate);
  };

  /** Selecciona el módulo y pasa al paso de procesos. @param {object} mod Módulo @returns {void} */
  const handlePickModule = (mod) => {
    setSelModule(mod);
    setShowAll(false);
  };

  /** Vuelve del paso de procesos al de módulos. @returns {void} */
  const handleBackModule = () => {
    setSelModule(null);
    setShowAll(false);
  };

  /** Fija el scope con el proceso elegido y habilita el chat. @param {object} proc Proceso @returns {void} */
  const handlePickProcess = (proc) => {
    const nextScope = `${selModule.MODURLXX}.${proc.PRODURLX}`.toLowerCase();
    setScope(nextScope);
    setSelProc(proc);
    setMessages((prev) => [
      ...prev,
      { role: "assistant", text: `Perfecto, pregúntame sobre **${selModule.MODDESXX} › ${proc.PRODESXX}**` },
    ]);
  };

  /** Reinicia la selección y la conversación para cambiar de módulo. @returns {void} */
  const handleChangeModule = () => {
    setScope(null);
    setSelModule(null);
    setSelProc(null);
    setShowAll(false);
    setMessages([]);
    setConversationId(null);
    setPending(null);
    setNavigate(null);
  };

  /** Redirige al módulo/proceso indicado por el agente, respetando el menú del usuario. @returns {void} */
  const handleNavigate = () => {
    const menuModule = menuData?.find((m) => m.MODURLXX === navigate.modUrl);
    const menuProcess = menuModule?.CIMS0004?.find((p) => p.PRODURLX === navigate.proUrl);
    if (!menuModule || !menuProcess) {
      setMessages((prev) => [...prev, { role: "assistant", text: "No tienes acceso a esa vista." }]);
      setNavigate(null);
      return;
    }
    dispatch(setModuleSelected(menuModule));
    dispatch(setProcesSelected(menuProcess));
    setNavigate(null);
  };

  /** Muestra un error del backend como mensaje en el chat. @param {unknown} err Error @returns {void} */
  const showError = (err) => {
    const text = err?.data?.errors?.message ?? "Ocurrió un error. Intenta de nuevo.";
    setMessages((prev) => [...prev, { role: "assistant", text: `⚠️ ${text}` }]);
  };

  /** Envía un texto al agente. @param {string} raw Texto a enviar @returns {Promise<void>} */
  const sendMessage = async (raw) => {
    const text = raw.trim();
    if (!text || !scope || sending) return;
    setMessages((prev) => [...prev, { role: "user", text }]);
    try {
      const res = await chatAgent({ scope, conversationId, message: text }).unwrap();
      applyResult(res.data);
    } catch (err) {
      showError(err);
    }
  };

  /** Envía la sugerencia elegida (con el dato capturado, si aplicaba). @param {object} sug Sugerencia @param {string} [value] Dato capturado @returns {void} */
  const handleSuggestionSubmit = (sug, value) => {
    if (sug.field) {
      if (!value) return;
      sendMessage(sug.build(value));
      return;
    }
    sendMessage(sug.label);
  };

  /** Confirma o rechaza la acción pendiente. @param {"allow"|"deny"} decision @returns {Promise<void>} */
  const handleConfirm = async (decision) => {
    try {
      const res = await confirmAgent({ conversationId, decision }).unwrap();
      setPending(null);
      applyResult(res.data);
    } catch (err) {
      setPending(null);
      showError(err);
    }
  };

  const modules = (menuData ?? []).filter((m) => ACTIVE_MODULES.includes(m.MODURLXX?.toLowerCase()));
  const allowedProcs = ACTIVE_PROCESSES[selModule?.MODURLXX?.toLowerCase()] ?? [];
  const processes = (selModule?.CIMS0004 ?? []).filter((p) =>
    allowedProcs.includes(p.PRODURLX?.toLowerCase())
  );
  const options = selModule ? processes : modules;
  const visible = showAll ? options : options.slice(0, MAX_VISIBLE);
  const suggestions = scope ? DEFAULT_QUESTIONS[scope] ?? [] : [];

  const viewMessages = messages.map((m) => ({
    role: m.role,
    content:
      m.role === "assistant" ? (
        <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{m.text}</ReactMarkdown>
      ) : (
        m.text
      ),
  }));

  const pickerOptions = !scope
    ? visible.map((o) =>
        selModule
          ? { key: o.PROIDXXX, label: o.PRODESXX }
          : { key: o.MODIDXXX, label: o.MODDESXX }
      )
    : undefined;

  /** Resuelve la opción elegida en el picker (módulo o proceso, según el paso). @param {string|number} key Id elegido @returns {void} */
  const handlePickOption = (key) => {
    if (selModule) {
      const proc = processes.find((p) => p.PROIDXXX === key);
      if (proc) handlePickProcess(proc);
      return;
    }
    const mod = modules.find((m) => m.MODIDXXX === key);
    if (mod) handlePickModule(mod);
  };

  return (
    <ChatboxxView
      messages={viewMessages}
      isBusy={sending || confirming}
      disabled={sending}
      scopeLabel={scope && selModule && selProc ? `Módulo: ${selModule.MODDESXX} › ${selProc.PRODESXX}` : undefined}
      onChangeScope={scope ? handleChangeModule : undefined}
      suggestions={suggestions}
      onSuggestionSubmit={handleSuggestionSubmit}
      pickerTitle={!scope ? (selModule ? "¿Sobre cuál proceso?" : "Sobre cuál módulo quieres preguntarme") : undefined}
      pickerOptions={pickerOptions}
      onPickOption={handlePickOption}
      onBack={!scope && selModule ? handleBackModule : undefined}
      canShowMore={!scope && options.length > MAX_VISIBLE}
      showAll={showAll}
      onToggleShowAll={() => setShowAll((v) => !v)}
      navigateLabel={navigate ? navigate.label || "Ir a la vista" : undefined}
      onNavigate={handleNavigate}
      pendingSummary={pending ? pending.summary : undefined}
      onConfirm={handleConfirm}
      confirming={confirming}
    />
  );
}
